/**
 * Report-Definition für den Projektabschluss.
 *
 * Übersetzt den gespeicherten Datenstand der Abschluss-Seite in ein
 * {@link ReportDocument}. Analog zu {@link ./projektauftragReport}: deklariert
 * nur den Inhalt, der gemeinsame Renderer erzeugt das PDF.
 */
import {
  buildDocument,
  buildSection,
  keyValuesBlock,
  kv,
  tableBlock,
  textBlock,
  type ReportBlock,
  type ReportDocument,
} from '../../../lib/pdf/reportModel';

export type AbschlussStatus = 'offen' | 'in_prep' | 'abgenommen';

export interface ChecklistEntry {
  item: string;
  done: boolean;
  signedBy: string | null;
}

export interface ProjektabschlussReportInput {
  // Kopf
  status: AbschlussStatus;
  datum: string | null; // ISO yyyy-mm-dd
  unterzeichner: string;
  // Lessons Learned
  lessonsPositiv: string;
  lessonsNegativ: string;
  // Abnahme-Checkliste
  checkliste: ChecklistEntry[];
  // Übergabe
  uebergabeDoku: string;
}

const STATUS_LABEL: Record<AbschlussStatus, string> = {
  offen: 'Offen',
  in_prep: 'In Vorbereitung',
  abgenommen: 'Abgenommen',
};

/** 'YYYY-MM-DD' -> 'DD.MM.YYYY'; leere/ungültige Werte -> ''. */
function formatDate(iso: string | null): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

export function buildProjektabschlussReport(input: ProjektabschlussReportInput): ReportDocument {
  const checklistBlock: ReportBlock =
    input.checkliste.length > 0
      ? tableBlock(
          [
            { header: 'Abnahme-Punkt', weight: 3 },
            { header: 'Erledigt', weight: 1 },
            { header: 'Unterzeichnet von', weight: 2.4 },
          ],
          input.checkliste.map((c) => [
            c.item || '—',
            c.done ? 'Ja' : 'Nein',
            (c.signedBy ?? '').trim() || '—',
          ]),
        )!
      : { kind: 'note', value: 'Noch keine Einträge.' };

  return buildDocument([
    buildSection('Abschluss', [
      keyValuesBlock([
        kv('Status', STATUS_LABEL[input.status]),
        kv('Abschlussdatum', formatDate(input.datum)),
        kv('Unterzeichner', input.unterzeichner),
      ]),
    ]),
    buildSection('Lessons Learned', [
      textBlock('Was lief gut?', input.lessonsPositiv),
      textBlock('Was sollte beim nächsten Mal anders laufen?', input.lessonsNegativ),
    ]),
    { title: 'Abnahme-Checkliste', blocks: [checklistBlock] },
    buildSection('Übergabe-Dokumentation', [
      textBlock('Übergabe', input.uebergabeDoku),
    ]),
  ]);
}
